import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, KeyRound, LogOut, Menu, Moon, Sun, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/primitives.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/menu.jsx';
import { useAuth } from '@/routes/AuthProvider.jsx';
import { useTheme } from '@/hooks/useTheme.jsx';
import { ROLE_LABELS } from '@/lib/constants.js';

/**
 * Breadcrumbs from the path.
 *
 * Derived rather than declared per route, so a new screen gets them for free.
 * The trade-off is that an id segment reads as an id — mitigated by
 * `resolveSegment`, which the detail screens use to substitute a wallet address
 * for the ObjectId in the trail.
 */
const LABELS = {
  users: 'Users',
  stakes: 'Stakes',
  withdrawals: 'Withdrawals',
  income: 'Income',
  packages: 'Packages',
  ranks: 'Ranks',
  'upline-levels': 'Upline levels',
  'audit-log': 'Audit log',
  system: 'System',
  account: 'Account',
  password: 'Change password',
};

const humanise = (segment) =>
  LABELS[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

function Breadcrumbs() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (!segments.length) {
    return <span className="text-sm font-medium">Dashboard</span>;
  }

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1 text-sm">
        <li>
          <Link to="/" className="text-muted-foreground transition-colors hover:text-foreground">
            Dashboard
          </Link>
        </li>

        {segments.map((segment, index) => {
          const href = `/${segments.slice(0, index + 1).join('/')}`;
          const isLast = index === segments.length - 1;

          /*
           * A 24-character hex string is a Mongo id. Shown truncated rather
           * than in full — the trail is for orientation, and 24 characters of
           * hex pushes the useful part of the crumb off a narrow screen.
           */
          const isId = /^[a-f0-9]{24}$/i.test(segment);
          const label = isId ? `${segment.slice(0, 8)}…` : humanise(segment);

          return (
            <li key={href} className="flex min-w-0 items-center gap-1">
              <ChevronRight
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              {isLast ? (
                <span className="truncate font-medium" aria-current="page">
                  {label}
                </span>
              ) : (
                <Link
                  to={href}
                  className="truncate text-muted-foreground transition-colors hover:text-foreground"
                >
                  {label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function Topbar({ onOpenSidebar }) {
  const { admin, logout, isSuperAdmin } = useAuth();
  const { isDark, toggle } = useTheme();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
      >
        <Menu />
      </Button>

      <Breadcrumbs />

      <div className="ml-auto flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggle}
          aria-label={isDark ? 'Switch to the light theme' : 'Switch to the dark theme'}
        >
          {isDark ? <Sun /> : <Moon />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <UserCircle className="h-4 w-4" aria-hidden="true" />
              <span className="hidden max-w-[12rem] truncate sm:inline">{admin?.name}</span>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="normal-case">
              <p className="truncate text-sm font-medium text-foreground">{admin?.name}</p>
              <p className="truncate text-xs font-normal text-muted-foreground">{admin?.email}</p>
              <Badge variant={isSuperAdmin ? 'default' : 'info'} className="mt-1.5">
                {ROLE_LABELS[admin?.role] ?? admin?.role}
              </Badge>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link to="/account/password">
                <KeyRound />
                Change password
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem onSelect={() => logout()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>

            {/*
             * Distinct from the above: this revokes every refresh family, which
             * is what an operator wants after signing in on a machine they no
             * longer control.
             */}
            <DropdownMenuItem onSelect={() => logout({ allDevices: true })} destructive>
              <LogOut />
              Sign out everywhere
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Topbar;
